import type { ApiResponse, PaginationMeta } from '@nest-recruit/shared';

/**
 * The one way the browser talks to the API.
 *
 * Same-origin in production (the API serves the SPA) and proxied by Vite in
 * development, so there is no base URL to configure and the session cookie rides
 * along on its own — no token to store, nothing for a script to steal.
 *
 * Every response is unwrapped from the `{ success, data, meta }` envelope here, so
 * no screen ever writes `response.data.data`. Every failure becomes an `ApiError`
 * carrying the machine-readable `code` a screen branches on and the field errors it
 * attaches to inputs (§L26: validation is inline, next to the field).
 */

const BASE = '/api/v1';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields: Readonly<Record<string, string>>;
  /** Anything else the server attached — S19's double-booking warnings, say. */
  readonly details: Readonly<Record<string, unknown>>;
  readonly requestId: string | undefined;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    fields?: Record<string, string>;
    details?: Readonly<Record<string, unknown>>;
    requestId?: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields ?? {};
    this.details = init.details ?? {};
    this.requestId = init.requestId;
  }

  /** The session is gone — the shell turns this into a redirect with a return URL. */
  get isUnauthenticated(): boolean {
    return this.status === 401 && this.code !== 'two_factor_required';
  }

  get needsTwoFactor(): boolean {
    return this.code === 'two_factor_required';
  }
}

export interface ApiResult<TData> {
  readonly data: TData;
  readonly meta: PaginationMeta | undefined;
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Appended as a query string; undefined and empty values are dropped. */
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  /** S17: the candidate's test page sends its link's token in a header, never the address. */
  readonly headers?: Readonly<Record<string, string>>;
}

/** An API address for the browser to load itself — an `<img>` source, say. */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

export async function request<TData>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResult<TData>> {
  const url = `${BASE}${path}${queryString(options.query)}`;

  let response: Response;
  try {
    // A file upload (S13's career-site logo) goes as the browser builds it: the
    // multipart boundary is in a content-type only the browser can write.
    const isForm = options.body instanceof FormData;
    response = await fetch(url, {
      method: options.method ?? 'GET',
      headers: { ...(options.body === undefined || isForm ? {} : { 'content-type': 'application/json' }), ...options.headers },
      ...(options.body === undefined ? {} : { body: isForm ? options.body : JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
      // Same-origin, but stated rather than assumed: the session cookie is the
      // whole authentication mechanism and must not depend on a browser default.
      credentials: 'same-origin',
    });
  } catch {
    // A network failure is not a server error, and saying "something went wrong on
    // our side" when the user's wifi dropped sends them looking in the wrong place.
    throw new ApiError({
      code: 'network_unreachable',
      message: "Couldn't reach the server. Check your connection and try again.",
      status: 0,
    });
  }

  if (response.status === 204) {
    return { data: undefined as TData, meta: undefined };
  }

  const payload = (await response.json().catch(() => null)) as ApiResponse<TData> | null;

  if (!payload) {
    throw new ApiError({
      code: 'unreadable_response',
      message: "Something went wrong on our side. Try again — nothing you typed was lost.",
      status: response.status,
    });
  }

  if (!payload.success) {
    throw new ApiError({
      code: payload.error.code,
      message: payload.error.message,
      status: response.status,
      fields: (payload.error.details?.fields as Record<string, string> | undefined) ?? {},
      ...(payload.error.details ? { details: payload.error.details } : {}),
      ...(payload.error.requestId ? { requestId: payload.error.requestId } : {}),
    });
  }

  return { data: payload.data, meta: payload.meta };
}

/** For the common case where nothing needs the pagination meta. */
export async function apiGet<TData>(
  path: string,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<TData> {
  return (await request<TData>(path, { ...options, method: 'GET' })).data;
}

export async function apiPost<TData>(path: string, body?: unknown): Promise<TData> {
  return (await request<TData>(path, { method: 'POST', body })).data;
}

export async function apiPatch<TData>(path: string, body?: unknown): Promise<TData> {
  return (await request<TData>(path, { method: 'PATCH', body })).data;
}

export async function apiPut<TData>(path: string, body?: unknown): Promise<TData> {
  return (await request<TData>(path, { method: 'PUT', body })).data;
}

export async function apiDelete<TData>(path: string, body?: unknown): Promise<TData> {
  return (await request<TData>(path, { method: 'DELETE', body })).data;
}

function queryString(
  query: Readonly<Record<string, string | number | boolean | undefined>> | undefined,
): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

/** Turns any thrown value into something with a message worth showing a person. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message !== '') return error.message;
  return "Something went wrong. Try again — nothing you typed was lost.";
}

/** The first field's own reason, for a toast with no field on screen to highlight (D85); else errorMessage. */
export function fieldErrorMessage(error: unknown): string {
  return (error instanceof ApiError ? Object.values(error.fields)[0] : undefined) ?? errorMessage(error);
}
